import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { getMenu } from "../../../../../services/Admin/SalesScreenApi";
import { resolveImageUrl } from "../../../../../services/indexMediaBaseUrl";
import { useAuth } from "../../../../../Context/AuthContext";
import { changeKitchenItemsStatus } from "../../../../../services/Admin/OrderProcessingScreensApi";
import { handleBrowserPrintPrepration } from "./PreprationPrint/printUtilsPrepration";

const itemStatusLabel = {
  in_prepration: "في التحضير",
  in_preparation: "في التحضير",
  pending: "جديد لم يحضر",
  pickup: "تم التسليم الي العميل",
};

const getItemStatusText = (status) => itemStatusLabel[status] || status || "-";

const itemStatusColor = {
  in_prepration: "warning.main",
  in_preparation: "warning.main",
  pending: "error.main",
  pickup: "success.main",
};

const getItemStatusColor = (status) => itemStatusColor[status] || "text.secondary";

const CASHIER_DELETE_ITEM_LIMIT = 99;
const PREPARATION_CATEGORY_IDS = [12, 13];
const PREPARATION_CATEGORY_NAMES = ["BAKERY", "DEASERT"];

const toSafeAmount = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const getItemUnitValue = (item) => {
  const price = toSafeAmount(item?.price);
  if (price) return price;

  const total = toSafeAmount(item?.total);
  const quantity = toSafeAmount(item?.quantity);
  if (total && quantity) return total / quantity;

  return 0;
};

const getOrderTableNumber = (order) =>
  order?.table?.number ||
  order?.table_number ||
  order?.table_no ||
  order?.table?.name ||
  order?.table_name ||
  order?.table_id ||
  "";

const OrderItemsSection = ({
  order,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onCloseTime,
  onTotalItemsValueChange,
  onProductsMapReady, // ✅ جديد
  onPreparationItemStatusUpdated,

}) => {
  const [products, setProducts] = useState([]);
  const [preparationPrintItemId, setPreparationPrintItemId] = useState(null);
  const { hasPermission, userRole, userType } = useAuth();
  const isCashierAdmin = userType === "admin" && userRole === "cashier";

  // Fetch menu and products
useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await getMenu();
        if (Array.isArray(response.data)) {
          const allProducts = response.data.flatMap((cat) => (
            (cat.products || []).map((product) => ({
              ...product,
              category_id: product.category_id ?? cat.id,
              category: product.category || {
                id: cat.id,
                name: cat.name,
                name_en: cat.name_en,
                code: cat.code,
                slug: cat.slug,
              },
            }))
          ));
          setProducts(allProducts);

          // ✅ ابنِ map وابعته للأب
          const map = {};
          allProducts.forEach((p) => {
            map[p.id] = p; // نخزن المن منيو كامل (name/image/...)
          });
          onProductsMapReady?.(map);
        }
      } catch (error) {
        console.error("Error fetching menu:", error);
      }
    };

    fetchMenu();
  }, [onProductsMapReady]);

  const getProductById = (productId) => {
    if (!productId) return null;
    return products.find((product) => String(product.id) === String(productId)) || null;
  };

  // Helper function to get product name by id
  const getProductName = (productId) => {
    if (!productId) return null;
    const product = getProductById(productId);
    return product ? product.name : null;
  };

  // Helper function to get product image by id
  const getProductImage = (productId) => {
    if (!productId) return null;
    const product = getProductById(productId);
    return product?.image ? resolveImageUrl(product.image) : null;
  };

  const isBakeryPreparationItem = (item) => {
    const product = item?.product || getProductById(item?.product_id);
    const category = product?.category || item?.product?.category || null;
    const categoryId = product?.category_id ?? category?.id ?? item?.product?.category_id;

    if (PREPARATION_CATEGORY_IDS.includes(Number(categoryId))) return true;

    const categoryNames = [
      category?.name,
      category?.name_en,
      category?.code,
      category?.slug,
      product?.category_name,
      product?.categoryName,
    ];

    return categoryNames.some((name) => (
      PREPARATION_CATEGORY_NAMES.includes(String(name || "").trim().toUpperCase())
    ));
  };

  const orderItems = Array.isArray(order?.order_items) ? order.order_items : [];
  const totalItemsValue = orderItems.reduce((sum, item) => {
    const normalizedTotal = String(item.total ?? "").replace(/,/g, "").trim();
    const parsedTotal = Number(normalizedTotal);
    return sum + (Number.isFinite(parsedTotal) ? parsedTotal : 0);
  }, 0);

  const canDeleteItem = (item) => {
    if (isCashierAdmin) {
      return getItemUnitValue(item) <= CASHIER_DELETE_ITEM_LIMIT;
    }

    return hasPermission("delete");
  };

  const handlePrintPreparationItem = async (item, itemName, itemType) => {
    if (!item?.id) {
      alert("لا يوجد معرف صالح للصنف.");
      return;
    }

    setPreparationPrintItemId(item.id);

    const preparationItem = {
      ...item,
      product_name: itemName,
      product_type_name: itemType,
      quantity: item.quantity,
      note: item.note || "",
      added_at: item.created_at || item.open_time || item.added_at,
    };

    const orderForPrint = {
      ...(order || {}),
      order_number: order?.general_increment || order?.id,
      table_number: getOrderTableNumber(order),
      preparation_print_time: new Date().toISOString(),
      items: [preparationItem],
      order_items: [preparationItem],
      meta: {
        ...(order?.meta || {}),
        tableNumber: getOrderTableNumber(order),
      },
    };

    try {
      const didOpenPrint = await handleBrowserPrintPrepration(
        orderForPrint,
        {},
        order?.id,
      );

      if (didOpenPrint === false) {
        return;
      }

      await changeKitchenItemsStatus([item.id], "in_prepration");
      onPreparationItemStatusUpdated?.(item.id, "in_prepration");
    } catch (error) {
      console.error("Error printing preparation item:", error);
      alert(error?.message || "حدث خطأ أثناء طباعة التجهيز أو تحديث حالة الصنف.");
    } finally {
      setPreparationPrintItemId(null);
    }
  };

  useEffect(() => {
    onTotalItemsValueChange?.(totalItemsValue);
  }, [totalItemsValue, onTotalItemsValueChange]);

  if (!order) return null;

  return (
    <Box sx={{ mb: 2, direction: 'rtl' }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Button 
          variant="contained" 
          size="small" 
          startIcon={<AddIcon />}
          onClick={onAddItem}
        >
          إضافة صنف
        </Button>
        <Typography variant="subtitle2">العناصر</Typography>
      </Box>
      {orderItems.length > 0 ? (
        <TableContainer component={Paper} dir="rtl">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>اسم الصنف</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>السعر</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>الكمية</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>الاجمالي</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>نوع</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>الإجراءات</TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {orderItems.map((it) => {
                // Determine if it's a product or custom drink
                const isCustomDrink = !it.product && it.drinks;
                const itemName = isCustomDrink 
                  ? (it.drinks?.drink_name || it.drinks?.name || `مخصص #${it.drink_id}`)
                  : (it.product?.name || getProductName(it.product_id) || `من منيو #${it.product_id}`);
                const itemType = isCustomDrink ? 'مخصص' : 'من منيو';
                const itemImage = isCustomDrink ? null : (it.product?.image ? resolveImageUrl(it.product.image) : getProductImage(it.product_id));
                const canPrintPreparation = it.status === "pending" && isBakeryPreparationItem(it);

                return (
                  <TableRow key={it.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Product Image */}
                        {itemImage && (
                          <Box
                            component="img"
                            src={itemImage}
                            alt={itemName}
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '1px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {itemName}
                          </Typography>
                        {isCustomDrink && it.drinks?.is_approved === 0 && (
                          <Typography variant="caption" sx={{ color: 'warning.main' }}>
                            ⏳ قيد المراجعة
                          </Typography>
                        )}
                        {/* Custom drink details */}
                        {isCustomDrink && it.drinks && (
                          <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #eee' }}>
                            {it.drinks.temperature && (
                              <Typography variant="caption" display="block">
                                🌡️ درجة الحرارة: {it.drinks.temperature}°
                              </Typography>
                            )}
                            {it.drinks.description && (
                              <Typography variant="caption" display="block">
                                📝 {it.drinks.description}
                              </Typography>
                            )}
                            {/* Display materials if available */}
                            {it.drinks.materials && Array.isArray(it.drinks.materials) && it.drinks.materials.length > 0 && (
                              <Box sx={{ mt: 0.3 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }} display="block">
                                  المكونات:
                                </Typography>
                                {it.drinks.materials.slice(0, 3).map((mat, idx) => (
                                  <Typography key={idx} variant="caption" display="block" sx={{ pl: 0.5 }}>
                                    • {mat.material?.name || `مادة #${mat.material_id}`}: {mat.quantity} {mat.material?.measurment_unit?.abbreviation || ''}
                                  </Typography>
                                ))}
                                {it.drinks.materials.length > 3 && (
                                  <Typography variant="caption" display="block" sx={{ fontStyle: 'italic', pl: 0.5 }}>
                                    ... و {it.drinks.materials.length - 3} مكونات أخرى
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                        {it.note && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.3, color: 'info.main' }}>
                            💬 {it.note}
                          </Typography>
                        )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{it.price}</TableCell>
                    <TableCell align="center">{it.quantity}</TableCell>
                    <TableCell>{it.total}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="caption"
                        sx={{ color: getItemStatusColor(it.status), fontWeight: 700 }}
                      >
                        {getItemStatusText(it.status)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{itemType}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {canPrintPreparation && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<PrintIcon fontSize="small" />}
                            onClick={() => handlePrintPreparationItem(it, itemName, itemType)}
                            disabled={preparationPrintItemId === it.id}
                            sx={{ minWidth: 0, px: 1, whiteSpace: "nowrap" }}
                          >
                            {preparationPrintItemId === it.id ? "جاري..." : "طباعة للتجهيز"}
                          </Button>
                        )}
                        {/* Close Time Icon - Only show if open_time exists and close_time is null */}
                        {it.open_time && !it.close_time && (
                          <IconButton 
                            size="small"
                            onClick={() => onCloseTime(it)}
                            sx={{ color: 'success.main' }}
                            title="إنهاء وقت التجهيز"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                        {hasPermission("edit") && (
                        <IconButton 
                          size="small"
                          onClick={() => onEditItem(it)}
                          sx={{ color: 'primary.main' }}
                          title="تعديل"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                          )}
                          {canDeleteItem(it) && (
                        <IconButton 
                          size="small"
                          onClick={() => onDeleteItem(it)}
                          sx={{ color: 'error.main' }}
                          title="حذف"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                          )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                  إجمالي قيمة الأصناف
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {totalItemsValue.toFixed(2)}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2">لا توجد عناصر في هذا الطلب</Typography>
      )}


    </Box>
  );
};

export default OrderItemsSection;
